import _ from 'lodash';

/**
 * Structural clone for post-data payloads — replaces `_.cloneDeep(this.form.value)`
 * in the formAsPostData serialisers.
 *
 * Only plain-object/array CONTAINERS are copied; every other value (primitives,
 * Date, moment, File, functions, class instances) passes through by reference.
 *
 * Why the containers must be copied: the post-data pipeline structurally mutates
 * them (`_.set` / `_.unset` / `delete` / property assignment), and FormGroup.value's
 * cached tree shares those containers with live control state — mutating them
 * in place would corrupt the form.
 *
 * Why the leaves are safe by reference: the pipeline never mutates a non-plain
 * leaf in place — convertDatesNew rebuilds the whole tree into fresh plain
 * objects (dot.dot / dot.object) before the destructive removeDeletedTasks /
 * removeEmptyObjects passes run. Meanwhile `_.cloneDeep` was pathologically
 * cloning the entire Arrangement/Service class graphs reachable through the
 * '_arrangement' control and select-option controls (~800ms per call), only for
 * those branches to be deleted or replaced immediately after.
 *
 * NOTE: this is NOT the form-STRUCTURE clone. form-structure.ts cloneDeeps its
 * structure templates at build time — that fixed shared-select-option corruption
 * and must stay as `_.cloneDeep`.
 *
 * The plain-object test MUST stay `_.isPlainObject` (a prototype check) — never
 * an instanceof / constructor-name list — so class instances of any kind are
 * passed by reference rather than walked.
 *
 * The tracker is a MEMO MAP (container -> its copy), not a visited Set. That
 * matters for ALIASING, not just cycles: the same plain object can be reachable
 * at two different paths in `form.value` (e.g. the same account facility object
 * held by both deceased.placeOfPassing.hospitalOrNursingHome and
 * deceased.usualResidenceFacility). A visited Set returns the SECOND occurrence
 * by live reference, and the destructive passes downstream — getValueFromOptionValue
 * does `_.unset(data, `${path}._meta`)` — would then mutate live form and
 * account state. `_.cloneDeep` memoises, so master was immune; a Set was a
 * behaviour change, not a like-for-like swap. Returning the same copy for the
 * same input also preserves the aliasing shape of the payload itself.
 */
export function clonePostData<T>(value: T, seen?: Map<object, any>): T {
  if (Array.isArray(value)) {
    const tracker = seen ?? new Map<object, any>();
    const memo = tracker.get(value);
    if (memo !== undefined) {
      return memo as T;
    }

    // Register the copy BEFORE walking children, so a cycle back to this array
    // resolves to the copy instead of recursing to a stack overflow.
    const copy: any[] = [];
    tracker.set(value, copy);

    for (const item of value) {
      copy.push(clonePostData(item, tracker));
    }

    return copy as unknown as T;
  }

  if (_.isPlainObject(value)) {
    const tracker = seen ?? new Map<object, any>();
    const memo = tracker.get(value as unknown as object);
    if (memo !== undefined) {
      return memo as T;
    }

    const copy: any = {};
    tracker.set(value as unknown as object, copy);

    for (const key of Object.keys(value as any)) {
      // A plain-assigned own '__proto__' key would silently set the copy's
      // prototype instead of creating an own property (lodash guards this via
      // defineProperty). Server data is trusted, but the guard is one line.
      if (key === '__proto__') {
        continue;
      }
      copy[key] = clonePostData((value as any)[key], tracker);
    }

    return copy as T;
  }

  return value;
}
